import dayjs, { type Dayjs } from 'dayjs';
import { useResolvedFeatureFlags } from '@/shared/hooks';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Stack, type SxProps, Text, type Theme, ToggleGroup, ToggleItem } from '@/ui';
import { DatePicker } from '@/ui/primitives/DatePicker';
import type { DashboardRange } from '@/features/dashboard/hooks';
import type { ReactElement } from 'react';

type DashboardDateRangeControlsProps = {
  value: DashboardRange;
  onChange: (nextRange: DashboardRange) => void;
  includeLatest?: boolean;
  fullWidthOnMobile?: boolean;
  stretchOnDesktop?: boolean;
  sx?: SxProps<Theme>;
  buttonSx?: SxProps<Theme>;
  displayStartDay?: string | null;
  displayEndDay?: string | null;
  isCustomRange?: boolean;
  onSelectedStartDayChange?: (nextDay: string | null) => void;
  onSelectedEndDayChange?: (nextDay: string | null) => void;
  showDatePickers?: boolean;
  showQuickFilters?: boolean;
  showQuickFiltersLabel?: boolean;
};

export const DashboardDateRangeControls = ({
  value,
  onChange,
  includeLatest = false,
  fullWidthOnMobile = false,
  stretchOnDesktop = false,
  sx,
  buttonSx,
  displayStartDay,
  displayEndDay,
  isCustomRange = false,
  onSelectedStartDayChange,
  onSelectedEndDayChange,
  showDatePickers = true,
  showQuickFilters = true,
  showQuickFiltersLabel = true,
}: DashboardDateRangeControlsProps): ReactElement | null => {
  const { flags } = useResolvedFeatureFlags();
  const dateFiltersEnabled = flags?.dateFilters ?? true;
  if (!dateFiltersEnabled) {
    return null;
  }
  const toggleValue = isCustomRange ? null : value;
  const pickerTextColorKey = isCustomRange ? 'textPrimary' : 'textSecondary';
  const pickerBorderColorKey = isCustomRange ? 'accent' : 'border';
  const restoreScroll = (): void => {
    const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
    if (scrollY === null) return;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, behavior: 'auto' });
      });
    } else {
      window.setTimeout(() => {
        window.scrollTo({ top: scrollY, behavior: 'auto' });
      }, 0);
    }
  };
  return (
    <Stack
      direction={fullWidthOnMobile ? { xs: 'column', sm: 'row' } : 'row'}
      alignItems={fullWidthOnMobile ? { xs: 'stretch', sm: 'center' } : 'center'}
      gap="sm"
      sx={{
        width: fullWidthOnMobile
          ? { xs: '100%', sm: stretchOnDesktop ? '100%' : 'auto' }
          : stretchOnDesktop
            ? '100%'
            : undefined,
        justifyContent: stretchOnDesktop ? { xs: 'flex-start', sm: 'space-between' } : undefined,
      }}
    >
      {showDatePickers && (onSelectedStartDayChange || onSelectedEndDayChange) ? (
        <Stack
          direction="row"
          alignItems="center"
          gap="lg"
          sx={{ width: fullWidthOnMobile ? { xs: '100%', sm: 'auto' } : undefined }}
        >
          <Stack direction="row" alignItems="center" gap="md">
            <Text role="label" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
              From
            </Text>
            <DatePicker
              value={displayStartDay ? dayjs(displayStartDay) : null}
              onChange={(nextValue: Dayjs | null) => {
                const nextDay = nextValue ? nextValue.format('YYYY-MM-DD') : null;
                onSelectedStartDayChange?.(nextDay);
                restoreScroll();
              }}
              maxDate={displayEndDay ? dayjs(displayEndDay) : dayjs()}
              placeholder="dd/mm/yyyy"
              openPickerButtonSx={{
                ml: `-${s('sm')}`,
                mr: s('none'),
                '& svg': {
                  mr: s('none'),
                },
              }}
              textFieldSx={{
                width: 128,
                '& .MuiPickersInputBase-root': {
                  height: 32,
                  minHeight: 32,
                  paddingRight: '2px',
                  color: (theme) => resolveSemanticColor(theme, pickerTextColorKey),
                },
                '& .MuiPickersInputBase-sectionContent': {
                  color: (theme) => resolveSemanticColor(theme, pickerTextColorKey),
                },
                '& .MuiPickersInputBase-input': { paddingY: 0 },
                '& .MuiPickersSectionList-root': {
                  height: '100%',
                  alignItems: 'center',
                  paddingLeft: s('sm'),
                  paddingRight: s('sm'),
                },
                '& .MuiPickersInputBase-sectionsContainer': {
                  height: '100%',
                  paddingLeft: s('md'),
                  paddingRight: s('sm'),
                },
                '& .MuiPickersOutlinedInput-notchedOutline': {
                  borderColor: (theme) => resolveSemanticColor(theme, pickerBorderColorKey),
                },
                '&:hover .MuiPickersOutlinedInput-notchedOutline': {
                  borderColor: (theme) => resolveSemanticColor(theme, pickerBorderColorKey),
                },
              }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" gap="md">
            <Text role="label" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
              To
            </Text>
            <DatePicker
              value={displayEndDay ? dayjs(displayEndDay) : null}
              onChange={(nextValue: Dayjs | null) => {
                const nextDay = nextValue ? nextValue.format('YYYY-MM-DD') : null;
                onSelectedEndDayChange?.(nextDay);
                restoreScroll();
              }}
              minDate={displayStartDay ? dayjs(displayStartDay) : undefined}
              maxDate={dayjs()}
              placeholder="dd/mm/yyyy"
              openPickerButtonSx={{
                ml: `-${s('sm')}`,
                mr: s('none'),
                '& svg': {
                  mr: s('none'),
                },
              }}
              textFieldSx={{
                width: 128,
                '& .MuiPickersInputBase-root': {
                  height: 32,
                  minHeight: 32,
                  paddingRight: '2px',
                  color: (theme) => resolveSemanticColor(theme, pickerTextColorKey),
                },
                '& .MuiPickersInputBase-sectionContent': {
                  color: (theme) => resolveSemanticColor(theme, pickerTextColorKey),
                },
                '& .MuiPickersInputBase-input': { paddingY: 0 },
                '& .MuiPickersSectionList-root': {
                  height: '100%',
                  alignItems: 'center',
                  paddingLeft: s('sm'),
                  paddingRight: s('sm'),
                },
                '& .MuiPickersInputBase-sectionsContainer': {
                  height: '100%',
                  paddingLeft: s('md'),
                  paddingRight: s('sm'),
                },
                '& .MuiPickersOutlinedInput-notchedOutline': {
                  borderColor: (theme) => resolveSemanticColor(theme, pickerBorderColorKey),
                },
                '&:hover .MuiPickersOutlinedInput-notchedOutline': {
                  borderColor: (theme) => resolveSemanticColor(theme, pickerBorderColorKey),
                },
              }}
            />
          </Stack>
        </Stack>
      ) : null}
      {showQuickFilters ? (
        <Stack
          direction="row"
          alignItems="center"
          gap="md"
          sx={
            showDatePickers && (onSelectedStartDayChange || onSelectedEndDayChange)
              ? {
                  marginLeft: stretchOnDesktop
                    ? { xs: s('none'), sm: s('none') }
                    : { xs: s('none'), sm: s('xl') },
                }
              : undefined
          }
        >
          {showQuickFiltersLabel ? (
            <Text role="label" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
              Quick filters
            </Text>
          ) : null}
          <ToggleGroup
            size="small"
            value={toggleValue}
            onChange={(_event, nextValue: DashboardRange | null) => {
              if (!nextValue) return;
              onChange(nextValue);
              restoreScroll();
            }}
            aria-label="Dashboard date range"
            sx={
              [
                {
                  width: 'auto',
                  display: 'inline-flex',
                  minHeight: 32,
                  height: 32,
                  '& .MuiToggleButton-root': {
                    paddingX: s('lg'),
                    paddingY: s('xxs'),
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    minWidth: 'auto',
                    minHeight: 32,
                    height: 32,
                    lineHeight: 1,
                    flex: '0 0 auto',
                  },
                  '& .MuiToggleButton-root .MuiTypography-root': {
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  },
                },
                ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
              ].filter(Boolean) as SxProps<Theme>
            }
          >
            {includeLatest ? (
              <ToggleItem value="latest" sx={buttonSx}>
                <Text role="body" component="span">
                  Latest
                </Text>
              </ToggleItem>
            ) : null}
            <ToggleItem value="7d" sx={buttonSx}>
              <Text role="body" component="span">
                7d
              </Text>
            </ToggleItem>
            <ToggleItem value="14d" sx={buttonSx}>
              <Text role="body" component="span">
                14d
              </Text>
            </ToggleItem>
            <ToggleItem value="30d" sx={buttonSx}>
              <Text role="body" component="span">
                30d
              </Text>
            </ToggleItem>
            <ToggleItem value="all" sx={buttonSx}>
              <Text role="body" component="span">
                All
              </Text>
            </ToggleItem>
          </ToggleGroup>
        </Stack>
      ) : null}
    </Stack>
  );
};
